import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { Location, locationZ } from '@libs/schemas';
import { z } from 'zod';
import { useToast } from 'src/components/useToast';
import { cn } from '@/lib/utils';
import Autocomplete from 'react-google-autocomplete';
import { GOOGLE_MAPS_API_KEY } from 'src/config';

// Extend the location schema to include optional fields needed for the form
const formLocationZ = locationZ.extend({
  _id: z.string().optional(),
});

type LocationZ = z.infer<typeof formLocationZ>;

type FormState = {
  isValid: boolean;
  isDirty: boolean;
  values: LocationZ;
};

interface LocationFormProps {
  location?: Location | null;
  onSuccess?: () => void;
  onStateChange?: (state: FormState) => void;
}

export interface LocationFormRef {
  submit: () => void;
  reset: () => void;
  isSelectingAddress?: boolean;
}

export const LocationForm = forwardRef<LocationFormRef, LocationFormProps>(
  ({ location, onSuccess, onStateChange }, ref) => {
    const { errorToast } = useToast();
    const [placeId, setPlaceId] = useState<string>('');
    const [addressInput, setAddressInput] = useState<string>(location?.address.line1 || '');
    const [isSelectingAddress, setIsSelectingAddress] = useState(false);

    const form = useForm<LocationZ>({
      resolver: zodResolver(formLocationZ),
      defaultValues: {
        name: location?.name || '',
        code: location?.code || '',
        isActive: location?.isActive ?? true,
        notes: location?.notes || '',
        address: {
          line1: location?.address.line1 || '',
          line2: location?.address.line2 || '',
          city: location?.address.city || '',
          state: location?.address.state || '',
          postalCode: location?.address.postalCode || '',
          country: location?.address.country || 'US',
        },
        geo: {
          lat: location?.geo.lat || 0,
          lng: location?.geo.lng || 0,
        },
      },
    });

    const { isValid, isDirty } = useFormState({ control: form.control });

    useEffect(() => {
      onStateChange?.({ isValid, isDirty, values: form.getValues() });
    }, [isValid, isDirty, form, onStateChange]);

    // Sync addressInput when location changes
    useEffect(() => {
      if (location?.address.line1) {
        setAddressInput(location.address.line1);
      }
    }, [location?.address.line1]);

    useImperativeHandle(ref, () => ({
      submit: () => form.handleSubmit(onSubmit)(),
      reset: () => form.reset(),
      isSelectingAddress,
    }));

    const onSubmit = async (data: LocationZ) => {
      try {
        if (data.geo.lat < -90 || data.geo.lat > 90) {
          errorToast({
            title: 'Invalid Latitude',
            description: 'Latitude must be between -90 and 90 degrees',
          });
          return;
        }

        if (data.geo.lng < -180 || data.geo.lng > 180) {
          errorToast({
            title: 'Invalid Longitude',
            description: 'Longitude must be between -180 and 180 degrees',
          });
          return;
        }

        if (data.address.country.length !== 2) {
          errorToast({
            title: 'Invalid Country Code',
            description: 'Country must be a 2-letter ISO code (e.g., US, CA, MX)',
          });
          return;
        }

        onSuccess?.();
      } catch (error) {
        errorToast({
          title: 'Error',
          description: 'An error occurred while saving the location',
        });
      }
    };

    return (
      <div className="space-y-8">
        <style>{`
          .pac-container {
            z-index: 10000 !important;
            position: fixed !important;
            background: white !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 6px !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
            pointer-events: auto !important;
          }
          .pac-item {
            cursor: pointer !important;
            padding: 8px 12px !important;
            border-bottom: 1px solid #f1f5f9 !important;
            pointer-events: auto !important;
          }
          .pac-item:hover {
            background-color: #f8fafc !important;
          }
          .pac-item:last-child {
            border-bottom: none !important;
          }
          .pac-item-query {
            font-size: 14px !important;
            padding-right: 3px !important;
            color: #000000 !important;
          }
          .pac-matched {
            font-weight: 700 !important;
          }
          .pac-item-selected,
          .pac-item-selected:hover {
            background-color: #e6f2ff !important;
          }
        `}</style>
        <Form {...form}>
          <form id="location-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Location Details Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Location Details</h3>
                <p className="text-sm text-muted">Basic location information</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Location Name
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter location name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel
                          className={cn(
                            'text-sm font-medium',
                            `${field.value ? 'text-text-primary' : 'text-muted'}`,
                          )}
                        >
                          Active Status
                        </FormLabel>
                        <div className="text-xs text-muted">Enable or disable this location</div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="data-[state=checked]:bg-primary"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter any additional notes about this location"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Address Information Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
                <p className="text-sm text-muted">Physical location address</p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="address.line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Address Line 1
                      </FormLabel>
                      <FormControl>
                        <Autocomplete
                          className="w-full rounded-md bg-white border border-input px-3 py-2 h-10 text-md text-text-light ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted/50 placeholder:text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          apiKey={GOOGLE_MAPS_API_KEY}
                          inputAutocompleteValue="off"
                          value={addressInput}
                          options={{
                            types: ['address'],
                            componentRestrictions: { country: 'us' },
                          }}
                          placeholder="Start typing an address"
                          onFocus={() => setIsSelectingAddress(true)}
                          onBlur={() => {
                            // Delay to allow click on dropdown
                            setTimeout(() => setIsSelectingAddress(false), 200);
                          }}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            setAddressInput(value);
                            field.onChange(value);

                            // Clear all other fields when address line 1 is cleared
                            if (!value || value.trim() === '') {
                              form.setValue('address.city', '');
                              form.setValue('address.state', '');
                              form.setValue('address.postalCode', '');
                              form.setValue('address.country', 'US');
                              form.setValue('geo.lat', 0);
                              form.setValue('geo.lng', 0);
                              setPlaceId('');
                            }
                          }}
                          onPlaceSelected={(place) => {
                            const getPlaceValue = (key: string) =>
                              place.address_components?.find((c) => c.types.includes(key))
                                ?.short_name as string;

                            // Extract address components
                            const streetNumber = getPlaceValue('street_number') || '';
                            const route = getPlaceValue('route') || '';
                            const addressLine1 =
                              streetNumber + (streetNumber && route ? ' ' : '') + route;
                            const city = getPlaceValue('locality') || '';
                            const state = getPlaceValue('administrative_area_level_1') || '';
                            const postalCode = getPlaceValue('postal_code') || '';
                            const country = getPlaceValue('country') || 'US';

                            // Extract coordinates
                            const lat = place.geometry?.location?.lat() || 0;
                            const lng = place.geometry?.location?.lng() || 0;

                            // Update form values and local state
                            if (addressLine1 && city && state && postalCode) {
                              setAddressInput(addressLine1);
                              form.setValue('address.line1', addressLine1);
                              form.setValue('address.city', city);
                              form.setValue('address.state', state);
                              form.setValue('address.postalCode', postalCode);
                              form.setValue('address.country', country);
                              form.setValue('geo.lat', lat);
                              form.setValue('geo.lng', lng);
                              setPlaceId(place.place_id || '');

                              // Trigger form validation
                              form.trigger([
                                'address.line1',
                                'address.city',
                                'address.state',
                                'address.postalCode',
                                'geo.lat',
                                'geo.lng',
                              ]);
                            } else {
                              errorToast({
                                title: 'Invalid Address',
                                description:
                                  'The selected address is missing required information. Please try a different address.',
                              });
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.line2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Address Line 2
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Enter apartment, suite, etc. (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="address.city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">City</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter city" disabled={!!placeId} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          State/Province
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter state or province"
                            disabled={!!placeId}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="address.postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">
                          Postal Code
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Enter postal code" disabled={!!placeId} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address.country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-muted">Country</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="US"
                            maxLength={2}
                            className="uppercase"
                            disabled={!!placeId}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Geolocation Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Geolocation</h3>
                <p className="text-sm text-muted">
                  {placeId
                    ? 'Coordinates automatically filled from Google Places selection above.'
                    : 'Enter the exact coordinates for this location. Coordinates will be automatically filled when you select an address using Google Places above, or you can enter them manually.'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="geo.lat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">Latitude</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          placeholder="40.7128"
                          disabled={!!placeId}
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="geo.lng"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">Longitude</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          placeholder="-74.0060"
                          disabled={!!placeId}
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
      </div>
    );
  },
);

LocationForm.displayName = 'LocationForm';
